import DoubleArrowTwoToneIcon from '@mui/icons-material/DoubleArrowTwoTone';
import AddIcon from '@mui/icons-material/Add';
import { Button, Grid, LinearProgress, Typography } from '@mui/material';
import styled from '@emotion/styled';
import { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'src/hooks';
import { useNavigate } from 'react-router';
import useModuleMetadata from 'src/hooks/use-module-metadata.hook';
import useMatomoFunction from 'src/hooks/use-matomo-function.hook';
import Storage from 'src/utils/storage/storage';
import LoadSavedScenarioDialog from '../pricing-simulator/components/load-saved-scenario-dialog/load-saved-scenario-dialog';
import { PPAStageInfoCard, CreateNewScenarioDialog } from './components';
import { Scenario } from '../scenarios/types/scenario.model';
import { setScenario } from './store/simulator-home-slice';
import CurrentScenarioInfoCard from './components/current-scenario-info-card/current-scenario-info-card';
import SimulatorFooter from '../components/simulation-footer/simulator-footer';
import {
  getAccessConfigForScenario,
  getListOfScenariosForTable,
  getOpportunityToColorMap
} from './store/simulator-home-async-thunks';
import MODULE_TO_PAGE_MAP from './types/simulation-module-to-page-map';
import useModulePreRequisites from '../common/hooks/use-module-pre-requisites.hook';
import DuplicateScenario from './duplicate-scenario';
import { MatomoEventTrackMapObject } from '../constants/constant';
import { getPhRecommendationConfig } from '../scenarios/store/scenario-slice';

const StyledGrid = styled(Grid)`
  height: 100%;
  width: 100%;
  background-image: url('/static/images/simulation-bg.svg');
  background-position: right top;
  background-repeat: no-repeat;
`;

function ArrowComponent() {
  return (
    <DoubleArrowTwoToneIcon
      color='primary'
      sx={{ marginX: 1, fontSize: '2.5rem' }}
    />
  );
}

const StyledButton = styled(Button)`
  background-color: #fcf1e8;
  border-radius: 10px;
  color: #ff6400;
`;

const getScenarioLandingRoute = (scenario: Scenario) => {
  return `/srm/simulator/${scenario.pk_id}/${
    (scenario?.planner_included ?? true) ? 'planner': 'innovation'
  }`;
}

export default function SimulationHome() {
  const appDispatch = useAppDispatch();
  const navigate = useNavigate();
  const { scenarioId, sourceId } = useModuleMetadata({
    module: MODULE_TO_PAGE_MAP.PLANNER_HOME
  });
  const { cardsSequence, isFetchingModuleSequence, currentScenario } =
    useAppSelector((state) => state.simulatorHome);

  const onPreRequisitesFetched = () => {
    appDispatch(getOpportunityToColorMap(sourceId));
  };

  const { callFunction } = useMatomoFunction();
  const getPageName = () => {
    const page_title = window.location.href.split('/').at(-1);
    return page_title;
  };
  useEffect(() => {
    callFunction(
      MatomoEventTrackMapObject.track_page_view.event_key,
      getPageName()
    );
  }, []);
  useEffect(() => {
    // get list of all the created scenarios
    appDispatch(
      getListOfScenariosForTable({
        sourceId
      })
    );
  }, []);

  const { isFetchingPreRequisites } = useModulePreRequisites({
    onPreRequisitesFetched,
    module: MODULE_TO_PAGE_MAP.PLANNER_HOME
  });
  const [isNewScenarioDialogOpen, setIsNewScenarioDialogOpen] = useState(false);

  const scenarioFormSubmitHandler = (scenario: Scenario) => {
    setIsNewScenarioDialogOpen(false);
    if (scenario) {
      appDispatch(setScenario(scenario));
      appDispatch(
        getAccessConfigForScenario({ scenarioId: scenario.pk_id, sourceId })
      ).then(() =>
        navigate(`${getScenarioLandingRoute(scenario)}?showSuccess=true`)
      );
    }
  };

  const createScenarioClickHandler = () => {
    setIsNewScenarioDialogOpen(true);
  };

  useEffect(() => {
    if (Number.isNaN(scenarioId)) {
      appDispatch(setScenario(null));
    }
  }, [scenarioId]);

  if (isFetchingPreRequisites) {
    return (
      <Grid container>
        <Grid item xs={12}>
          <LinearProgress />
        </Grid>
      </Grid>
    );
  }

  return (
    <StyledGrid>
      <Grid container sx={{ padding: (theme) => theme.spacing(1, 2) }}>
        <Grid container item xs={12} flexWrap='nowrap'>
          <Grid item xs='auto'>
            <Grid my={1}>
              <Typography variant='text3'>
                Start your simulation journey -{' '}
              </Typography>
            </Grid>
            <Grid container>
              <Grid item marginX={1} textAlign='center' xs='auto'>
                <StyledButton
                  onClick={createScenarioClickHandler}
                  sx={{ boxShadow: 3 }}
                  variant='contained'
                >
                  <AddIcon sx={{ fontSize: '2rem', margin: 2 }} />
                </StyledButton>
                <div>
                  <Typography variant='text1'>Create New Scenario</Typography>
                </div>
              </Grid>
              <Grid item marginX={1} textAlign='center' xs='auto'>
                <LoadSavedScenarioDialog />
              </Grid>
              <Grid item marginX={1} textAlign='center' xs='auto'>
                <DuplicateScenario />
              </Grid>
            </Grid>
          </Grid>
          {currentScenario ? (
            <Grid
              item
              xs='auto'
              ml={2}
              container
              sx={{ borderLeft: '1px solid #d9d9d9' }}
            >
              <Grid item xs={12} ml={2} my={1}>
                <Typography variant='text3'>
                  Continue your simulation journey -{' '}
                </Typography>
              </Grid>
              <Grid item xs={12} ml={2}>
                <CurrentScenarioInfoCard
                  scenarioSummaryStrip={currentScenario.summary_strip_data}
                  filters={JSON.parse(currentScenario.filter_data)}
                  scenarioName={currentScenario.name}
                />
              </Grid>
            </Grid>
          ) : null}
        </Grid>
        <Grid
          sx={{ height: '1px', backgroundColor: '#D9D9D9' }}
          item
          marginX={5}
          my={1}
          xs={12}
        />
        {isFetchingModuleSequence ? (
          <Grid xs={12}>
            <LinearProgress />
          </Grid>
        ) : (
          <Grid container item xs={12} justifyContent='center'>
            <Grid item xs={12} my={1}>
              <Typography variant='text3'>How it works?</Typography>
            </Grid>
            <Grid
              container
              item
              xs={12}
              justifyContent='center'
              flexWrap='nowrap'
            >
              <Grid item xs='auto'>
                <PPAStageInfoCard stages={cardsSequence.slice(0, 1)} />
              </Grid>
              <Grid
                container
                justifyContent='center'
                alignItems='center'
                item
                xs='auto'
                sx={{ height: '50vh' }}
              >
                <Grid item>
                  <ArrowComponent />
                </Grid>
              </Grid>
              <Grid item xs='auto'>
                <PPAStageInfoCard
                  stages={cardsSequence.slice(1, cardsSequence.length - 1)}
                />
              </Grid>
              <Grid
                container
                justifyContent='center'
                alignItems='center'
                item
                xs='auto'
                sx={{ height: '50vh' }}
              >
                <Grid item>
                  <ArrowComponent />
                </Grid>
              </Grid>
              <Grid item xs='auto'>
                <PPAStageInfoCard
                  stages={cardsSequence.slice(cardsSequence.length - 1)}
                />
              </Grid>
            </Grid>
          </Grid>
        )}
        {isNewScenarioDialogOpen ? (
          <CreateNewScenarioDialog
            isEditable={false}
            isDuplicate={false}
            currentScenario={null}
            isOpen={isNewScenarioDialogOpen}
            onClose={scenarioFormSubmitHandler}
          />
        ) : null}
      </Grid>
      <SimulatorFooter
        isSimulationDisabled={false}
        nextClickHandler={() => false}
        isButtonSectionVisible={false}
      />
    </StyledGrid>
  );
}
